import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle } from 'lucide-react';
import { PasswordInput } from '@/components/ui/password-input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    identifier: '',
    password: ''
  });
  const [showReset, setShowReset] = useState(false);
  const [resetStep, setResetStep] = useState('request');
  const [resetForm, setResetForm] = useState({
    email: '',
    code: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleResetChange = (e) => {
    setResetForm({
      ...resetForm,
      [e.target.name]: e.target.value
    });
    setResetError('');
    setResetSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const isPhone = /^[0-9+\-\s()]{6,}$/.test(formData.identifier.trim());

    try {
      if (isPhone) {
        const tenantResponse = await axios.post(`${BACKEND_URL}/api/tenant/login`, {
          phone: formData.identifier,
          password: formData.password
        });
        localStorage.setItem('tenantToken', tenantResponse.data.access_token);
        localStorage.setItem('tenant', JSON.stringify(tenantResponse.data.tenant));
        localStorage.removeItem('token');
        localStorage.removeItem('owner');
        navigate('/tenant/dashboard');
      } else {
        const response = await axios.post(`${BACKEND_URL}/api/auth/login`, formData);
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('owner', JSON.stringify(response.data.owner));
        localStorage.removeItem('tenantToken');
        localStorage.removeItem('tenant');
        if (['admin', 'superadmin', 'technician', 'housekeeping', 'laundry'].includes(response.data.owner.role)) {
          navigate('/admin');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Login gagal. Email/HP atau password salah.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetRequest = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    setResetError('');
    setResetSuccess('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/password-reset/request`, {
        email: resetForm.email
      });
      setResetSuccess(response.data.message);
      setResetStep('confirm');
    } catch (err) {
      setResetError(err.response?.data?.detail || 'Gagal mengirim kode reset.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetConfirm = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    setResetError('');
    setResetSuccess('');

    if (resetForm.newPassword !== resetForm.confirmPassword) {
      setResetError('Konfirmasi password baru tidak cocok.');
      setResetLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/password-reset/confirm`, {
        email: resetForm.email,
        code: resetForm.code,
        new_password: resetForm.newPassword
      });
      setResetSuccess(response.data.message);
      setFormData((prev) => ({
        ...prev,
        identifier: resetForm.email,
        password: ''
      }));
      setResetStep('request');
      setResetForm({
        email: '',
        code: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      setResetError(err.response?.data?.detail || 'Gagal mereset password.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-isk-bg flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <img src="/isk-logo.png" alt="ISK" className="h-16 w-auto mx-auto mb-2" />
          <p className="text-isk-muted">Property Management System</p>
        </div>

        {/* Login Card */}
        <div className="glass-card rounded-2xl p-8" data-testid="login-card">
          <h2 className="text-2xl font-bold text-isk-text mb-6">Login</h2>

          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 flex items-start" data-testid="error-message">
              <AlertCircle className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="identifier" className="text-isk-text font-semibold">
                Email atau Nomor HP
              </Label>
              <Input
                id="identifier"
                name="identifier"
                type="text"
                required
                value={formData.identifier}
                onChange={handleChange}
                placeholder="email@example.com atau 08xxxxxxxxxx"
                className="glass-input border-2 focus:border-isk-teal rounded-xl"
                data-testid="input-identifier"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-isk-text font-semibold">
                Password
              </Label>
              <PasswordInput
                id="password"
                name="password"
                required
                value={formData.password}
                onChange={handleChange}
                placeholder="Masukkan password"
                className="glass-input border-2 focus:border-isk-teal rounded-xl"
                data-testid="input-password"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-isk-teal hover:bg-isk-dark text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg transition-all disabled:opacity-50"
              data-testid="login-button"
            >
              {loading ? 'Masuk...' : 'Masuk'}
            </Button>
          </form>

          <div className="mt-4 text-right">
            <a href="/daftar" className="text-sm font-semibold text-isk-teal hover:text-isk-dark transition-colors">
              Daftar sebagai Tenant →
            </a>
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-isk-teal hover:text-isk-dark transition-colors"
            >
              ← Kembali ke Homepage
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Login;
